import { createMemo, For, type JSX, Show } from "solid-js";
import { cn } from "@space/shared";

/**
 * "Pill" tab bar — SINGLE source.
 *
 * The same ~20-line block was copied verbatim in the cohort detail view, the
 * student record, and the people directory (same classes, same
 * `role="tablist"`, same `data-testid` pattern). It lives here once; the
 * markup and the `data-testid`s are identical to the originals so as not to
 * break the e2e tests.
 *
 * Motion (deliberate containment):
 *   - Feedback on press: `active:scale-[0.97]` on the trigger. Only
 *     `transform`, ~120 ms.
 *   - Panel entrance: {@link PillTabPanels} animates ONLY the ENTRANCE
 *     (200 ms, `opacity` + `transform`). There's no exit animation on
 *     purpose: these are work screens (attendance, grades, roster) and an
 *     `out-in` would double the perceived latency of the tab switch
 *     (~400 ms) without adding any information. The new content is in the
 *     DOM and clickable from the very first frame.
 *   - `prefers-reduced-motion` is handled by the global guard in
 *     `global.css`.
 */

/** `id` of a tab's trigger; also serves as `aria-labelledby`. */
function pillTabId(prefix: string, tabId: string): string {
  return `${prefix}-${tabId}`;
}

/** `id` of the panel associated with a tab bar. */
function pillTabPanelId(prefix: string): string {
  return `${prefix}-panel`;
}

export interface PillTab<T extends string> {
  id: T;
  /** Label already translated by the caller. */
  label: string;
}

interface PillTabsProps<T extends string> {
  tabs: readonly PillTab<T>[];
  value: T;
  onChange: (id: T) => void;
  /**
   * `data-testid` and `id` prefix: each trigger ends up as
   * `${testIdPrefix}-${tab.id}`.
   */
  testIdPrefix: string;
  /** Accessible label for the bar (`aria-label` of the `tablist`). */
  label: string;
  /** Optional content to the right of the label (e.g. a counter). */
  trailing?: (id: T) => JSX.Element;
  class?: string;
}

export function PillTabs<T extends string>(props: PillTabsProps<T>) {
  const items = createMemo<readonly PillTab<T>[]>((prev = []) => {
    const next = props.tabs;
    if (
      prev.length === next.length &&
      prev.every((p, i) => p.id === next[i]?.id && p.label === next[i]?.label)
    ) {
      return prev;
    }
    return next;
  });

  return (
    <div
      class={cn("flex gap-1 overflow-x-auto rounded-xl bg-muted/40 p-1", props.class)}
      role="tablist"
      aria-label={props.label}
    >
      <For each={items()}>
        {(tabDef) => {
          const isSelected = () => props.value === tabDef.id;
          return (
            <button
              type="button"
              role="tab"
              id={pillTabId(props.testIdPrefix, tabDef.id)}
              data-testid={pillTabId(props.testIdPrefix, tabDef.id)}
              aria-selected={isSelected()}
              aria-controls={pillTabPanelId(props.testIdPrefix)}
              onClick={() => {
                props.onChange(tabDef.id);
              }}
              class={cn(
                "flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold",
                // Only `color`/`background-color` at rest and `transform` on
                // press: neither one forces layout.
                "transition-[color,background-color,transform] duration-150 ease-out active:scale-[0.97]",
                isSelected()
                  ? "bg-surface shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tabDef.label}
              {props.trailing?.(tabDef.id)}
            </button>
          );
        }}
      </For>
    </div>
  );
}

interface PillTabPanelsProps {
  /** Active tab. Changing it remounts the panel and triggers the entrance. */
  value: string;
  /** Same prefix that was passed to {@link PillTabs}. */
  testIdPrefix: string;
  children: JSX.Element;
  class?: string;
}

/**
 * Container for the active panel. When `value` changes, the `<Show keyed>`
 * remounts the wrapper and the `@keyframes list-entrance` plays once.
 */
export function PillTabPanels(props: PillTabPanelsProps) {
  return (
    <Show when={props.value} keyed>
      {(key) => (
        <div
          id={pillTabPanelId(props.testIdPrefix)}
          role="tabpanel"
          aria-labelledby={pillTabId(props.testIdPrefix, key)}
          data-tab-panel={key}
          class={cn("animate-list-entrance", props.class)}
        >
          {props.children}
        </div>
      )}
    </Show>
  );
}
